<template>
  <div class="pr-5">
    <v-row no-gutters>
      <v-col cols="12">
        <card-title-row translation-path="sidebar.modernization.title" class="pt-5" :has-buttons="false">
          <template #subtitle>
            <italic-subheader translation-path="sidebar.modernization.subtitle" class="text-left" />
          </template>
        </card-title-row>
      </v-col>
    </v-row>
    <sidebar-form ref="form" v-model="printParams" :inputs="inputs" :is-editing-on="!isStickyButtonsLoading">
      <template #geometry="scope">
        <sidebar-geometry-input v-bind="scope" v-model="printParams.geometry" :is-valid.sync="isGeometryValid" />
      </template>
    </sidebar-form>
  </div>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';

export default {
  name: 'Modernization',
  components: {
    CardTitleRow: () => import('@/components/CardTitleRow'),
    ItalicSubheader: () => import('@/components/ItalicSubheader'),
    SidebarForm: () => import('@/components/SidebarForm'),
    SidebarGeometryInput: () => import('@/components/SidebarGeometryInput'),
  },
  data: () => ({
    isGeometryValid: false,
    printParams: {
      scale: 500,
      dpi: 150,
    },
    printingLayersAttributes: ['labels_visible', 'layer_id', 'line_direction', 'opacity', 'style', 'visible', 'type'],
    currentTaskId: null,
  }),
  created() {
    if (!this.isLightingHeadDataSource) {
      this.$router.push({
        name: 'map',
      });
    }
  },
  mounted() {
    this.$root.$off('print');
    this.$root.$on('print', this.generateReport);
    this.stickyButtons = [
      {
        action: 'print',
        translationPath: 'sidebar.modernization.generateRaport',
      },
    ];
    this.isStickyButtonsVisible = true;
    this.isStickyButtonsValid = false;
  },
  beforeDestroy() {
    if (this.currentTaskId) {
      this.cancelModernizationReport(this.currentTaskId);
    }
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = false;
  },
  computed: {
    project: get('layers/project'),
    currentLayerDataSource: sync('layers/currentLayer@data_source_name'),
    lightingHeadDataSource: get('admin/modulesMapping@zdm_data.datasources.head.datasource'),
    isOpenStreetMapVisible: get('map/isOpenStreetMapVisible'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    selectedFeatures: get('map/selectedFeatures'),
    currentLayerId: sync('layers/currentLayer@id'),
    isLightingHeadDataSource() {
      return this.currentLayerDataSource === this.lightingHeadDataSource;
    },
    inputs() {
      return [
        {
          name: 'geometry',
          dataType: { name: 'text' },
          geometryType: 'LineString',
          icon: 'mdi-map-marker',
          hint: this.$i18n.t('sidebar.nmtHint'),
          hasNameColumn: false,
          cols: '12',
          hasDivider: true,
        },
        {
          name: 'scale',
          dataType: { name: 'select' },
          items: [
            { text: '1:250', value: 250 },
            { text: '1:500', value: 500 },
            { text: '1:1000', value: 1000 },
            { text: '1:2000', value: 2000 },
            { text: '1:5000', value: 5000 },
            { text: '1:10000', value: 10000 },
          ],
          translationPath: 'sidebar.prints.scale',
          cols: '5',
          nameCols: '7',
        },
        {
          name: 'dpi',
          dataType: { name: 'integer' },
          translationPath: 'sidebar.prints.dpi',
          subtitlePath: 'sidebar.prints.dpiSubtitle',
          hasSubtitle: true,
          cols: '5',
          nameCols: '7',
          minValue: 1,
          allowNegative: false,
        },
      ];
    },
  },
  watch: {
    isGeometryValid(nV) {
      if (this.selectedFeatures[this.currentLayerId]) {
        this.isStickyButtonsValid = nV;
      }
    },
    selectedFeatures() {
      if (this.isGeometryValid && this.selectedFeatures[this.currentLayerId]) {
        this.isStickyButtonsValid = true;
      } else {
        this.isStickyButtonsValid = false;
      }
    },
    printParams: {
      deep: true,
      handler() {
        if (this.printParams.dpi) {
          this.isStickyButtonsValid = true;
        } else {
          this.isStickyButtonsValid = false;
        }
      },
    },
  },
  methods: {
    getModernizationReportId: call('zdm/getModernizationReportId'),
    checkModernizationReportStatus: call('celeryTasks/getCeleryTaskStatus'),
    generateModernizationReport: call('celeryTasks/getCeleryTaskResult'),
    cancelModernizationReport: call('celeryTasks/stopCeleryTask'),
    async generateReport() {
      this.isStickyButtonsLoading = true;
      const layers = [];
      for (const projectLayer of JSON.parse(JSON.stringify(this.$_getFlatGroupsLayers(this.project.layers).layers))) {
        for (const key of Object.keys(projectLayer)) {
          if (!this.printingLayersAttributes.includes(key)) {
            delete projectLayer[key];
          }
        }
        layers.push(projectLayer);
      }
      const payload = {
        ...this.printParams,
        layers,
        orientation: 'vertical',
        geometry: this.printParams.geometry,
        ids: this.selectedFeatures[this.currentLayerId],
      };
      if (this.isOpenStreetMapVisible) {
        payload.basemap = 'OpenStreetMap';
      }
      try {
        const taskId = await this.getModernizationReportId(payload);
        this.currentTaskId = taskId;
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.generatingReport'),
        });
        const checkerId = Date.now();
        this.$store.set('asyncStatusChecker/PUSH_STATUS_CHECKER!', {
          id: checkerId,
          checkingStatusFunction: async () => {
            try {
              const status = await this.checkModernizationReportStatus(taskId);
              if (['REVOKED', 'FAILURE'].includes(status)) {
                this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
                return 'cancel';
              }
              return status === 'SUCCESS';
            } catch (error) {
              this.currentTaskId = null;
              console.log('error', error);
            }
          },
          successCallback: async () => {
            this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
            try {
              await this.generateModernizationReport({ id: taskId });
              this.currentTaskId = null;
              this.isStickyButtonsLoading = false;
            } catch (error) {
              this.currentTaskId = null;
              this.isStickyButtonsLoading = false;
              console.log('error', error);
            }
          },
          interval: 5000,
        });
      } catch (error) {
        this.currentTaskId = null;
        this.isStickyButtonsLoading = false;
        console.log('error', error);
      }
    },
  },
};
</script>
